import { useMemo } from 'react';

import type { Node } from 'reactflow';

import { transformToReactFlowEdges, transformToReactFlowNodes } from '@/shared/lib/lineage/transform';
import type { LineageData, LineageFilterState, LineageHierarchyType } from '@/shared/types/lineage';
import type { LineageNodeData } from '@/shared/types/lineageNode';

import type { PlatformLineageResponse } from '@/features/lineage/api';

const NODE_WIDTH = 400;
const NODE_HEIGHT = 100;
const HORIZONTAL_SPACING = 100;
const VERTICAL_SPACING = 150;
const NODES_PER_ROW = 3;

export const usePlatformLineageData = (lineageData: PlatformLineageResponse | undefined) => {
  const data = useMemo(() => {
    if (!lineageData) {
      return { nodes: {}, upstreamEdges: {}, downstreamEdges: {} } as LineageData;
    }

    const transformedNodes = Object.entries(lineageData.nodes).reduce(
      (acc, [fqn, node]) => {
        acc[fqn] = {
          entity: node.entity as LineageData['nodes'][string]['entity'],
          nodeDepth: 0,
        };
        return acc;
      },
      {} as LineageData['nodes']
    );

    return {
      nodes: transformedNodes,
      upstreamEdges: lineageData.upstreamEdges,
      downstreamEdges: lineageData.downstreamEdges,
    } as LineageData;
  }, [lineageData]);

  const initialNodes = useMemo(() => {
    if (!data?.nodes) {
      return [];
    }

    const emptyFilterState: LineageFilterState = {
      depth: 5,
      direction: 'both',
      entityTypes: [],
      viewOptions: {
        selectedHierarchies: new Set<LineageHierarchyType>(),
      },
    };

    const nodes = transformToReactFlowNodes(data, emptyFilterState) as Node<LineageNodeData>[];

    const edges = Object.values(data.upstreamEdges || {}).concat(Object.values(data.downstreamEdges || {}));
    const connectedNodeIds = new Set<string>();
    edges.forEach((edge) => {
      if (edge.fromEntity) connectedNodeIds.add(edge.fromEntity.fullyQualifiedName);
      if (edge.toEntity) connectedNodeIds.add(edge.toEntity.fullyQualifiedName);
    });

    const isolatedNodes: Node<LineageNodeData>[] = [];
    const connectedNodes: Node<LineageNodeData>[] = [];

    nodes.forEach((node) => {
      if (connectedNodeIds.has(node.id)) {
        connectedNodes.push(node);
      } else {
        isolatedNodes.push(node);
      }
    });

    if (isolatedNodes.length === 0) {
      return connectedNodes;
    }

    let startX = 0;
    let startY = 0;
    if (connectedNodes.length > 0) {
      const minX = Math.min(...connectedNodes.map((node) => node.position.x));
      const maxY = Math.max(...connectedNodes.map((node) => node.position.y + NODE_HEIGHT));
      startX = minX;
      startY = maxY + VERTICAL_SPACING;
    }

    const layoutedIsolatedNodes = isolatedNodes.map((node, index) => {
      const row = Math.floor(index / NODES_PER_ROW);
      const col = index % NODES_PER_ROW;

      return {
        ...node,
        position: {
          x: startX + col * (NODE_WIDTH + HORIZONTAL_SPACING),
          y: startY + row * (NODE_HEIGHT + VERTICAL_SPACING),
        },
      };
    });

    return [...connectedNodes, ...layoutedIsolatedNodes];
  }, [data]);

  const visibleNodeIds = useMemo(() => new Set(initialNodes.map((node) => node.id)), [initialNodes]);

  const initialEdges = useMemo(() => {
    if (!data?.nodes) {
      return [];
    }
    return transformToReactFlowEdges(data, visibleNodeIds, false);
  }, [data, visibleNodeIds]);

  return {
    initialNodes,
    initialEdges,
  };
};
